// Pure-JSON equivalent of MemeExtension's runtime document rewrite, used by the
// static answer body path. By default [[content]] keeps its brackets around the
// linked content; compact previews can omit them. Existing prefix links get the
// meme styling class. Kept free of runtime imports so unit tests can load it
// without bundling the app graph.
import type { JSONContent } from '@tiptap/core'
import { memeHubPath } from 'lib/seoPaths'

// Legacy sentinel href. Meme links used to carry this instead of a URL, which
// made them invisible to crawlers (Google indexes the rendered DOM, and a
// non-URL href is not a link edge). New marks carry the real /meme/:text path;
// the sentinel is still recognised so stored content keeps working.
export const MEME_LINK_PREFIX = '@v2quest_self_'

// The crawlable href for a meme's hub, falling back to the sentinel only if the
// text cannot form a path.
export function memeLinkHref(content: string): string {
    return memeHubPath(content) || `${MEME_LINK_PREFIX}${content}`
}

// Matches the pattern MemeExtension's editor plugin rewrites at runtime.
const MEME_PATTERN = /\[\[\s*([\w\s]+)\s*\]\]/g

type MemeLinkMarksOptions = {
    showBrackets?: boolean
}

function memeLinkMark(content: string, memeClass?: string) {
    return {
        type: 'link',
        attrs: {
            href: memeLinkHref(content),
            ...(memeClass ? { class: memeClass } : {}),
        },
    }
}

function transformTextNode(
    node: JSONContent,
    memeClass?: string,
    showBrackets = true,
): JSONContent[] {
    const text = node.text || ''
    const segments: JSONContent[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    MEME_PATTERN.lastIndex = 0
    while ((match = MEME_PATTERN.exec(text)) !== null) {
        const content = match[1].trim()
        if (match.index > lastIndex) {
            segments.push({ ...node, text: text.slice(lastIndex, match.index) })
        }
        if (showBrackets) segments.push({ ...node, text: '[[' })
        segments.push({
            ...node,
            text: content,
            marks: [...(node.marks || []), memeLinkMark(content, memeClass)],
        })
        if (showBrackets) segments.push({ ...node, text: ']]' })
        lastIndex = match.index + match[0].length
    }

    if (segments.length === 0) return [node]
    if (lastIndex < text.length) {
        segments.push({ ...node, text: text.slice(lastIndex) })
    }
    return segments
}

function ensureMemeClassOnLinks(node: JSONContent, memeClass?: string): JSONContent {
    if (!memeClass || !node.marks) return node
    let changed = false
    const marks = node.marks.map(mark => {
        const href = (mark.attrs as { href?: string } | undefined)?.href
        if (mark.type === 'link' && href && href.startsWith(MEME_LINK_PREFIX)) {
            const existingClass = (mark.attrs?.class as string) || ''
            // Upgrade stored sentinel hrefs to the crawlable hub path.
            const crawlableHref = memeLinkHref(href.slice(MEME_LINK_PREFIX.length))
            if (!existingClass.includes(memeClass) || crawlableHref !== href) {
                changed = true
                return { ...mark, attrs: { href: crawlableHref, class: memeClass } }
            }
        }
        return mark
    })
    return changed ? { ...node, marks } : node
}

export function applyMemeLinkMarks(
    doc: JSONContent,
    memeClass?: string,
    { showBrackets = true }: MemeLinkMarksOptions = {},
): JSONContent {
    const walk = (node: JSONContent): JSONContent[] => {
        if (node.type === 'text' && node.text) {
            return transformTextNode(
                ensureMemeClassOnLinks(node, memeClass),
                memeClass,
                showBrackets,
            )
        }
        if (Array.isArray(node.content)) {
            return [{ ...node, content: node.content.flatMap(walk) }]
        }
        return [node]
    }
    return walk(doc)[0]
}
